import { useEffect, useRef } from "react"
import { DraftMarkdownSnippet } from "./DraftMarkdownSnippet"
import { wrapMarkdownBold, wrapMarkdownItalic } from "../utils/markdownBoldWrap.js"

interface MarkdownBoldTextareaProps {
  value: unknown;
  onChange: (next: string) => void;
  placeholder?: string;
  minHeight?: number;
  showPreview?: boolean;
}

export default function MarkdownBoldTextarea({
  value,
  onChange,
  placeholder = "",
  minHeight = 80,
  showPreview = true,
}: MarkdownBoldTextareaProps) {
  const taRef = useRef<HTMLTextAreaElement>(null)
  const safeValue = typeof value === "string" ? value : value == null ? "" : String(value)

  const recalcHeight = () => {
    const el = taRef.current
    if (!el) return
    el.style.height = "auto"
    el.style.height = `${Math.max(el.scrollHeight, minHeight)}px`
  }

  useEffect(() => {
    recalcHeight()
  }, [safeValue, minHeight])

  const applyBold = () => {
    const el = taRef.current
    if (!el) return
    const { nextText, selectionStart, selectionEnd } = wrapMarkdownBold(
      safeValue,
      el.selectionStart,
      el.selectionEnd
    )
    onChange(nextText)
    requestAnimationFrame(() => {
      const node = taRef.current
      if (!node) return
      node.focus()
      node.setSelectionRange(selectionStart, selectionEnd)
      recalcHeight()
    })
  }

  const applyItalic = () => {
    const el = taRef.current
    if (!el) return
    const { nextText, selectionStart, selectionEnd } = wrapMarkdownItalic(
      safeValue,
      el.selectionStart,
      el.selectionEnd
    )
    onChange(nextText)
    requestAnimationFrame(() => {
      const node = taRef.current
      if (!node) return
      node.focus()
      node.setSelectionRange(selectionStart, selectionEnd)
      recalcHeight()
    })
  }

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <button
          type="button"
          onClick={applyBold}
          className="px-3 py-1.5 text-sm font-medium bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
        >
          Bold
        </button>
        <button
          type="button"
          onClick={applyItalic}
          className="px-3 py-1.5 text-sm italic bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
        >
          Italic
        </button>
      </div>
      <textarea
        ref={taRef}
        value={safeValue}
        spellCheck
        onChange={(e) => {
          onChange(e.target.value)
          e.target.style.height = "auto"
          e.target.style.height = `${Math.max(e.target.scrollHeight, minHeight)}px`
        }}
        onKeyDown={(e) => {
          if ((e.metaKey || e.ctrlKey) && e.key === "b") {
            e.preventDefault()
            applyBold()
          }
          if ((e.metaKey || e.ctrlKey) && e.key === "i") {
            e.preventDefault()
            applyItalic()
          }
        }}
        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none font-mono"
        style={{
          minHeight: `${minHeight}px`,
          overflow: "hidden",
          height: "auto",
        }}
        placeholder={placeholder}
      />
      {showPreview ? (
        <div>
          <p className="text-xs font-medium text-gray-500 mb-1">Preview</p>
          <div className="border border-gray-200 rounded-md p-3 bg-gray-50 text-sm prose prose-sm max-w-none">
            <DraftMarkdownSnippet>{safeValue || " "}</DraftMarkdownSnippet>
          </div>
        </div>
      ) : null}
    </div>
  )
}
